import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useDialog } from '../../context/DialogContext'
import { AppLayout } from '../../components/layouts/AppLayout'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card'
import { ArrowLeft, Buildings, CheckCircle, Globe, MagnifyingGlass, EnvelopeSimple } from '@phosphor-icons/react'
import { createShipperWithInvite, createFreightForwarder, getCountryConfigs, CountryConfig } from '../../services/companies'
import { listTenants } from '../../services/tenants'
import { lookupCNPJ, formatCNPJ, formatAddressFromCNPJ } from '../../services/brasilapi'

type CompanyType = 'shipper' | 'freight-forwarder'

export default function NewCompanyPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { showSuccess, showError } = useDialog()
  const typeParam = searchParams.get('type') as CompanyType | null

  const [loading, setLoading] = useState(false)
  const [loadingCountries, setLoadingCountries] = useState(true)
  const [loadingCNPJ, setLoadingCNPJ] = useState(false)
  const [countries, setCountries] = useState<CountryConfig[]>([])
  const [tenants, setTenants] = useState<any[]>([])
  const [selectedCountry, setSelectedCountry] = useState<CountryConfig | null>(null)

  const [formData, setFormData] = useState({
    tenantId: '',
    companyType: (typeParam || 'shipper') as CompanyType,
    name: '',
    tradingName: '',
    countryCode: 'BR',
    primaryIdentifier: '',
    email: '',
    phone: '',
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  // Load countries and tenants
  useEffect(() => {
    async function loadData() {
      try {
        const [countriesData, tenantsData] = await Promise.all([
          getCountryConfigs(),
          listTenants()
        ])
        setCountries(countriesData)
        setTenants(tenantsData)

        // Set initial country (Brazil)
        const defaultCountry = countriesData.find(c => c.countryCode === 'BR')
        if (defaultCountry) {
          setSelectedCountry(defaultCountry)
        }

        setLoadingCountries(false)
      } catch (error) {
        console.error('Error loading data:', error)
        showError('Erro', 'Erro ao carregar dados')
        setLoadingCountries(false)
      }
    }
    loadData()
  }, [])

  // Update selected country when country code changes
  useEffect(() => {
    const country = countries.find(c => c.countryCode === formData.countryCode)
    setSelectedCountry(country || null)
  }, [formData.countryCode, countries])

  function validateForm() {
    const newErrors: Record<string, string> = {}

    // Tenant is only required for freight-forwarder (shipper creates its own tenant)
    if (formData.companyType === 'freight-forwarder' && !formData.tenantId) {
      newErrors.tenantId = 'Tenant é obrigatório'
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Nome da empresa é obrigatório'
    }

    if (!formData.primaryIdentifier.trim() && selectedCountry) {
      newErrors.primaryIdentifier = `${selectedCountry.identifierLabel} é obrigatório`
    }

    // Email is required for shippers (invite will be sent)
    if (formData.companyType === 'shipper' && !formData.email.trim()) {
      newErrors.email = 'Email é obrigatório para enviar o convite'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    if (!validateForm()) return

    try {
      setLoading(true)

      if (formData.companyType === 'shipper') {
        // Shipper: create with invite (new tenant will be created automatically)
        await createShipperWithInvite({
          companyData: {
            name: formData.name,
            tradingName: formData.tradingName || undefined,
            countryCode: formData.countryCode,
            primaryIdentifierType: selectedCountry?.primaryIdentifierType,
            primaryIdentifier: formData.primaryIdentifier,
            email: formData.email,
            phone: formData.phone || undefined,
          },
        })
        showSuccess('Sucesso', 'Embarcador criado com sucesso! Um convite foi enviado por email.')
      } else {
        // Freight Forwarder: use existing tenant
        const requestData = {
          tenantId: formData.tenantId,
          companyData: {
            name: formData.name,
            tradingName: formData.tradingName || undefined,
            countryCode: formData.countryCode,
            primaryIdentifierType: selectedCountry?.primaryIdentifierType,
            primaryIdentifier: formData.primaryIdentifier,
            email: formData.email || undefined,
            phone: formData.phone || undefined,
          },
        }
        await createFreightForwarder(requestData)
        showSuccess('Sucesso', 'Empresa criada com sucesso!')
      }

      navigate('/master/companies')
    } catch (error: any) {
      console.error('Error creating company:', error)
      showError('Erro', error.response?.data?.error || 'Erro ao criar empresa')
    } finally {
      setLoading(false)
    }
  }

  async function handleLookupCNPJ() {
    if (!formData.primaryIdentifier || formData.countryCode !== 'BR') {
      return
    }

    try {
      setLoadingCNPJ(true)
      const data = await lookupCNPJ(formData.primaryIdentifier)

      // Auto-fill form data
      setFormData(prev => ({
        ...prev,
        name: data.razao_social,
        tradingName: data.nome_fantasia || '',
        email: data.email || prev.email,
        phone: data.ddd_telefone_1 || prev.phone,
        primaryIdentifier: formatCNPJ(data.cnpj),
      }))

      showSuccess('Sucesso', 'Dados do CNPJ carregados com sucesso!')
    } catch (error: any) {
      console.error('Error looking up CNPJ:', error)
      showError('Erro', error.message || 'Erro ao buscar CNPJ')
    } finally {
      setLoadingCNPJ(false)
    }
  }

  if (loadingCountries) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Carregando...</div>
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/master/companies')}
                className="gap-2"
              >
                <ArrowLeft size={18} />
                Voltar
              </Button>
              <div className="h-6 w-px bg-gray-200" />
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Nova Empresa</h1>
                <p className="text-sm text-gray-500">
                  {formData.companyType === 'shipper' ? 'Embarcador' : 'Agente de Carga'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* Company Type & Tenant Selection */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Buildings size={20} className="text-emerald-600" weight="duotone" />
                  </div>
                  <div>
                    <CardTitle>Informações Básicas</CardTitle>
                    <CardDescription>
                      Configure o tenant e tipo de empresa
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Tenant Selection - only for freight forwarders */}
                  {formData.companyType === 'freight-forwarder' && (
                    <div>
                      <label htmlFor="tenantId" className="block text-sm font-medium text-gray-700 mb-2">
                        Tenant <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="tenantId"
                        value={formData.tenantId}
                        onChange={(e) => setFormData({ ...formData, tenantId: e.target.value })}
                        className={`w-full px-3 py-2 border rounded-md ${errors.tenantId ? 'border-red-500' : 'border-gray-200'}`}
                      >
                        <option value="">Selecione um tenant</option>
                        {tenants.map(tenant => (
                          <option key={tenant.id} value={tenant.id}>
                            {tenant.name}
                          </option>
                        ))}
                      </select>
                      {errors.tenantId && (
                        <p className="mt-1 text-sm text-red-600">{errors.tenantId}</p>
                      )}
                    </div>
                  )}

                  {/* Info for shippers - new tenant will be created */}
                  {formData.companyType === 'shipper' && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm text-amber-800">
                        <EnvelopeSimple size={16} className="inline mr-1" weight="fill" />
                        Um novo tenant sera criado automaticamente e um convite sera enviado por email para o responsavel.
                      </p>
                    </div>
                  )}

                  {/* Company Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tipo de Empresa <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-3">
                      <label className="flex-1">
                        <input
                          type="radio"
                          name="companyType"
                          value="shipper"
                          checked={formData.companyType === 'shipper'}
                          onChange={(e) => setFormData({ ...formData, companyType: e.target.value as CompanyType })}
                          className="sr-only"
                        />
                        <div className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                          formData.companyType === 'shipper'
                            ? 'border-emerald-600 bg-emerald-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}>
                          <div className="font-medium">Embarcador</div>
                          <div className="text-sm text-gray-500">Empresa que precisa de serviços de envio</div>
                        </div>
                      </label>
                      <label className="flex-1">
                        <input
                          type="radio"
                          name="companyType"
                          value="freight-forwarder"
                          checked={formData.companyType === 'freight-forwarder'}
                          onChange={(e) => setFormData({ ...formData, companyType: e.target.value as CompanyType })}
                          className="sr-only"
                        />
                        <div className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                          formData.companyType === 'freight-forwarder'
                            ? 'border-emerald-600 bg-emerald-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}>
                          <div className="font-medium">Agente de Carga</div>
                          <div className="text-sm text-gray-500">Empresa que fornece cotações de envio</div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Country & Company Details */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Globe size={20} className="text-blue-600" weight="duotone" />
                  </div>
                  <div>
                    <CardTitle>Dados da Empresa</CardTitle>
                    <CardDescription>
                      Informações da empresa e país de registro
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Country Selection */}
                  <div>
                    <label htmlFor="countryCode" className="block text-sm font-medium text-gray-700 mb-2">
                      País <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="countryCode"
                      value={formData.countryCode}
                      onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-md"
                    >
                      {countries.map(country => (
                        <option key={country.countryCode} value={country.countryCode}>
                          {country.countryName} ({country.countryCode})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Company Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Razão Social <span className="text-red-500">*</span>
                    </label>
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Nome legal da empresa"
                      className={errors.name ? 'border-red-500' : ''}
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                    )}
                  </div>

                  {/* Trading Name */}
                  <div>
                    <label htmlFor="tradingName" className="block text-sm font-medium text-gray-700 mb-2">
                      Nome Fantasia
                    </label>
                    <Input
                      id="tradingName"
                      type="text"
                      value={formData.tradingName}
                      onChange={(e) => setFormData({ ...formData, tradingName: e.target.value })}
                      placeholder="Nome comercial (opcional)"
                    />
                  </div>

                  {/* Primary Identifier (country-specific) */}
                  {selectedCountry && (
                    <div>
                      <label htmlFor="primaryIdentifier" className="block text-sm font-medium text-gray-700 mb-2">
                        {selectedCountry.identifierLabel} <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-2">
                        <Input
                          id="primaryIdentifier"
                          type="text"
                          value={formData.primaryIdentifier}
                          onChange={(e) => setFormData({ ...formData, primaryIdentifier: e.target.value })}
                          placeholder={selectedCountry.identifierExample}
                          className={errors.primaryIdentifier ? 'border-red-500' : ''}
                        />
                        {formData.countryCode === 'BR' && (
                          <Button
                            type="button"
                            onClick={handleLookupCNPJ}
                            disabled={loadingCNPJ || !formData.primaryIdentifier}
                            variant="outline"
                            className="flex items-center gap-2 whitespace-nowrap"
                          >
                            <MagnifyingGlass size={18} />
                            {loadingCNPJ ? 'Buscando...' : 'Buscar CNPJ'}
                          </Button>
                        )}
                      </div>
                      {errors.primaryIdentifier && (
                        <p className="mt-1 text-sm text-red-600">{errors.primaryIdentifier}</p>
                      )}
                      <p className="mt-1 text-sm text-gray-500">
                        Formato: {selectedCountry.identifierExample}
                        {formData.countryCode === 'BR' && ' • Clique em "Buscar CNPJ" para preencher automaticamente'}
                      </p>
                    </div>
                  )}

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      E-mail {formData.companyType === 'shipper' && <span className="text-red-500">*</span>}
                    </label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="contato@empresa.com"
                      className={errors.email ? 'border-red-500' : ''}
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                    )}
                    {formData.companyType === 'shipper' && (
                      <p className="mt-1 text-sm text-gray-500">
                        O convite de acesso sera enviado para este email
                      </p>
                    )}
                  </div>

                  {/* Phone */}
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Telefone
                    </label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+55 11 98765-4321"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Information Card */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-blue-600 flex-shrink-0 mt-0.5" weight="fill" />
                  <div className="text-sm text-blue-900">
                    <p className="font-medium mb-1">Suporte Multi-País</p>
                    <ul className="list-disc list-inside space-y-1 text-blue-800">
                      <li>Validação automática de documentos de acordo com o país</li>
                      <li>Suporte para {countries.length} países configurados</li>
                      <li>Foco principal: América do Sul e China</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/master/companies')}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {loading ? 'Criando...' : 'Criar Empresa'}
              </Button>
            </div>
          </div>
        </form>
      </main>
    </AppLayout>
  )
}
